import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import EventDialog from "@/components/EventDialog";

const HOUR_HEIGHT = 48;
const START_HOUR = 7;
const END_HOUR = 21;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  return x;
}
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmtDay(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
}
function fmtRange(start, end) {
  const s = start.toLocaleDateString(undefined, { month: "long", day: "numeric" });
  const e = end.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  return `${s} – ${e}`;
}
function timeLabel(h) {
  const hr = h % 12 === 0 ? 12 : h % 12;
  const ap = h < 12 ? "AM" : "PM";
  return `${hr} ${ap}`;
}
function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function CalendarPage() {
  const [view, setView] = useState("week"); // week | day
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [openEvent, setOpenEvent] = useState(false);
  const [defaultRange, setDefaultRange] = useState(null);
  const [search, setSearch] = useState("");

  const days = useMemo(() => {
    if (view === "day") return [cursor];
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor, view]);

  const reload = async () => {
    const [e, c] = await Promise.all([api.get("/events"), api.get("/contacts")]);
    setEvents(e.data);
    setContacts(c.data);
  };

  useEffect(() => {
    reload();
  }, []);

  const today = startOfDay(new Date());

  const move = (dir) => {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCursor(d);
  };

  const onSlotClick = (day, hour, e) => {
    if (e.target.closest("[data-event-block]")) return;
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    const end = new Date(start);
    end.setHours(hour + 1, 0, 0, 0);
    setEditing(null);
    setDefaultRange({ start, end });
    setOpenEvent(true);
  };

  const onEventClick = (ev) => {
    setEditing(ev);
    setDefaultRange(null);
    setOpenEvent(true);
  };

  const onSaved = (saved) => {
    setEvents((prev) => {
      const i = prev.findIndex((x) => x.id === saved.id);
      if (i >= 0) {
        const c = [...prev];
        c[i] = saved;
        return c;
      }
      return [...prev, saved];
    });
  };
  const onDeleted = (id) => setEvents((prev) => prev.filter((x) => x.id !== id));

  // Drag/resize
  const dragRef = useRef(null);
  const onDragStart = (e, ev, mode) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      mode,
      ev,
      startY: e.clientY,
      origStart: new Date(ev.start_at).getTime(),
      origEnd: new Date(ev.end_at).getTime(),
    };
    const move = (me) => {
      if (!dragRef.current) return;
      const dy = me.clientY - dragRef.current.startY;
      const deltaMs = Math.round(dy / HOUR_HEIGHT * 4) * 15 * 60000; // 15-min snap
      const { ev: orig, mode, origStart, origEnd } = dragRef.current;
      let start = origStart;
      let end = origEnd;
      if (mode === "move") {
        start += deltaMs;
        end += deltaMs;
      } else if (mode === "resize") {
        end = Math.max(origStart + 15 * 60000, origEnd + deltaMs);
      }
      const dur = end - start;
      // Live update local
      setEvents((prev) =>
        prev.map((x) => (x.id === orig.id ? { ...x, start_at: new Date(start).toISOString(), end_at: new Date(end).toISOString(), _dragging: true } : x)),
      );
      dragRef.current.live = { start, end, dur };
    };
    const up = async () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      if (!dragRef.current) return;
      const live = dragRef.current.live;
      const orig = dragRef.current.ev;
      dragRef.current = null;
      if (!live) {
        setEvents((prev) => prev.map((x) => (x._dragging ? { ...x, _dragging: false } : x)));
        return;
      }
      try {
        const payload = {
          title: orig.title,
          start_at: new Date(live.start).toISOString(),
          end_at: new Date(live.end).toISOString(),
          color: orig.color,
          contact_id: orig.contact_id,
          notes: orig.notes || "",
          reminder_label: orig.reminder_label || "",
          all_day: !!orig.all_day,
        };
        const r = await api.put(`/events/${orig.id}`, payload);
        setEvents((prev) => prev.map((x) => (x.id === orig.id ? r.data : x)));
      } catch (err) {
        toast.error("Failed to update event");
        reload();
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const eventsForDay = (d) => {
    const ds = startOfDay(d).getTime();
    const de = ds + 86400000;
    const list = events.filter((e) => {
      const t = new Date(e.start_at).getTime();
      return t >= ds && t < de;
    });
    if (search.trim()) {
      const s = search.toLowerCase();
      return list.filter((e) => e.title.toLowerCase().includes(s) || (e.notes || "").toLowerCase().includes(s));
    }
    return list;
  };

  const upcoming = useMemo(() => {
    const now = new Date();
    return events
      .filter((e) => new Date(e.start_at) >= now)
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
      .slice(0, 8);
  }, [events]);

  const headerLabel =
    view === "day"
      ? cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
      : fmtRange(days[0], days[6]);

  return (
    <div className="px-10 py-9 mr-fade-up">
      <header className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-3xl text-slate-900 tracking-tight">Calendar & Timeline</h1>
          <p className="text-sm text-slate-500 mt-1 font-body">{headerLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5">
            {["day", "week"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm rounded-md capitalize font-heading transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-600"}`}
                data-testid={`calendar-view-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCursor(startOfDay(new Date()))}
            className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-heading text-slate-700 hover:bg-slate-50"
            data-testid="calendar-today-button"
          >
            Today
          </button>
          <div className="inline-flex bg-white border border-slate-200 rounded-lg">
            <button onClick={() => move(-1)} className="px-2.5 py-2 hover:bg-slate-50 rounded-l-lg" data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => move(1)} className="px-2.5 py-2 hover:bg-slate-50 rounded-r-lg" data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <button
            onClick={() => {
              const now = new Date();
              now.setMinutes(0, 0, 0);
              const end = new Date(now.getTime() + 60 * 60 * 1000);
              setEditing(null);
              setDefaultRange({ start: now, end });
              setOpenEvent(true);
            }}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-heading font-medium hover:bg-slate-800 inline-flex items-center gap-2"
            data-testid="new-event-button"
          >
            <Plus className="w-4 h-4" />
            New event
          </button>
        </div>
      </header>

      <div className="flex gap-5">
        <div className="flex-1 mr-card overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 ml-1" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events…"
              className="flex-1 bg-transparent border-0 outline-none text-sm font-body placeholder:text-slate-400 py-1"
              data-testid="calendar-search-input"
            />
          </div>

          <div className="grid" style={{ gridTemplateColumns: `60px repeat(${days.length}, minmax(0, 1fr))` }}>
            <div />
            {days.map((d) => {
              const isToday = startOfDay(d).getTime() === today.getTime();
              return (
                <div key={d.toISOString()} className={`text-center py-3 border-b border-slate-100 ${isToday ? "" : ""}`}>
                  <div className={`text-xs uppercase tracking-[0.1em] ${isToday ? "text-slate-900 font-semibold" : "text-slate-400"} font-heading`}>
                    {d.toLocaleDateString(undefined, { weekday: "short" })}
                  </div>
                  <div className={`mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full ${isToday ? "bg-slate-900 text-white" : "text-slate-700"}`}>
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid relative" style={{ gridTemplateColumns: `60px repeat(${days.length}, minmax(0, 1fr))` }}>
            <div className="border-r border-slate-100">
              {HOURS.map((h) => (
                <div key={h} style={{ height: HOUR_HEIGHT }} className="text-[10px] text-slate-400 -mt-1.5 pr-2 text-right font-heading">
                  {timeLabel(h)}
                </div>
              ))}
            </div>
            {days.map((d) => (
              <DayColumn
                key={d.toISOString()}
                day={d}
                events={eventsForDay(d)}
                onSlotClick={onSlotClick}
                onEventClick={onEventClick}
                onDragStart={onDragStart}
              />
            ))}
          </div>
        </div>

        <aside className="w-80 mr-card p-5 self-start sticky top-6">
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-3 font-heading">Upcoming</div>
          <div className="space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-slate-400 font-body">No upcoming events.</div>}
            {upcoming.map((e) => (
              <button
                key={e.id}
                onClick={() => onEventClick(e)}
                className="w-full text-left px-3 py-2.5 rounded-lg border border-slate-100 bg-slate-50 hover:bg-white hover:border-slate-200 transition-colors"
                data-testid={`upcoming-event-${e.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                  <div className="font-heading text-sm text-slate-900 truncate">{e.title}</div>
                </div>
                <div className="text-xs text-slate-500 font-body">
                  {new Date(e.start_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} ·{" "}
                  {fmtTime(e.start_at)} – {fmtTime(e.end_at)}
                </div>
              </button>
            ))}
          </div>
        </aside>
      </div>

      <EventDialog
        open={openEvent}
        onClose={() => setOpenEvent(false)}
        event={editing}
        contacts={contacts}
        onSaved={onSaved}
        onDeleted={onDeleted}
        defaultStart={defaultRange?.start}
        defaultEnd={defaultRange?.end}
      />
    </div>
  );
}

function DayColumn({ day, events, onSlotClick, onEventClick, onDragStart }) {
  const totalMinutes = (END_HOUR - START_HOUR) * 60;
  return (
    <div className="relative border-r border-slate-100 last:border-r-0 mr-cal-grid" style={{ height: HOUR_HEIGHT * (END_HOUR - START_HOUR) }}>
      {HOURS.map((h) => (
        <div
          key={h}
          onClick={(e) => onSlotClick(day, h, e)}
          style={{ height: HOUR_HEIGHT }}
          className="cursor-pointer hover:bg-slate-50/60 transition-colors"
          data-testid={`slot-${day.toISOString().slice(0,10)}-${h}`}
        />
      ))}
      {events.map((e) => {
        const start = new Date(e.start_at);
        const end = new Date(e.end_at);
        const startMin = (start.getHours() - START_HOUR) * 60 + start.getMinutes();
        const endMin = (end.getHours() - START_HOUR) * 60 + end.getMinutes();
        const top = (startMin / 60) * HOUR_HEIGHT;
        const height = Math.max(20, ((endMin - startMin) / 60) * HOUR_HEIGHT - 2);
        return (
          <div
            key={e.id}
            data-event-block
            data-testid={`event-${e.id}`}
            onClick={(ev) => {
              ev.stopPropagation();
              if (!e._dragging) onEventClick(e);
            }}
            onMouseDown={(ev) => onDragStart(ev, e, "move")}
            className="absolute left-1 right-1 rounded-lg px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing shadow-sm overflow-hidden border border-white/40"
            style={{
              top,
              height,
              backgroundColor: `${e.color}1a`,
              borderLeft: `3px solid ${e.color}`,
            }}
          >
            <div className="font-heading font-semibold text-slate-900 truncate">{e.title}</div>
            <div className="text-slate-600 truncate">{fmtTime(e.start_at)} – {fmtTime(e.end_at)}</div>
            <div
              onMouseDown={(ev) => onDragStart(ev, e, "resize")}
              className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize"
            />
          </div>
        );
      })}
    </div>
  );
}
